import React from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
  Slide,
  Grow,
  alpha,
  Paper,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';


import { styled } from '@mui/material/styles';
import {
  LocationOn,
  Store,
  LocalHospital,
  Restaurant,
  LocalGroceryStore,
  Book,
  Pets,
  Spa,
  Explore,
  Search,
  Map,
  Star,
  Groups,
  TrendingUp,
  ConnectWithoutContact,
  Speed,
  Verified,
  Smartphone,
  PinDrop,
  QrCode,
  Notifications
} from '@mui/icons-material';
import { Nav } from 'react-bootstrap';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '90vh',
  background: `linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)`, // Changed to light background
  position: 'relative',
  overflow: 'hidden',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: '20px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)', // Lighter shadow
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.1)',
    borderColor: theme.palette.primary.main,
  }
}));

const CategoryIcon = styled(Box)(({ theme, color }) => ({
  width: 70,
  height: 70,
  borderRadius: '18px',
  background: alpha(color, 0.1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: color,
  margin: '0 auto',
  transition: 'all 0.3s ease',
}));

const ThedalOverview = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sdImage = `${process.env.PUBLIC_URL}/images/bg55.jpg`;
  // Core Features
  const features = [
    {
      icon: <LocationOn />,
      title: 'Location-Based Discovery',
      description: 'Find nearby shops, markets and hospitals based on your current location',
      color: theme.palette.primary.main
    },
    {
      icon: <Search />,
      title: 'Smart Search',
      description: 'Easily search for specific services or browse by categories',
      color: theme.palette.secondary.main
    },
    {
      icon: <Map />,
      title: 'Interactive Maps',
      description: 'Visual navigation to guide you to your destination',
      color: theme.palette.success.main
    },
    {
      icon: <Star />,
      title: 'Community Reviews',
      description: 'Read ratings and reviews from other users',
      color: theme.palette.warning.main
    }
  ];

  // Service Categories
  const categories = [
    {
      name: 'Super Markets',
      icon: <LocalGroceryStore />,
      description: 'Find groceries and household essentials nearby',
      color: theme.palette.primary.main
    },
    {
      name: 'Hospitals',
      icon: <LocalHospital />,
      description: 'Locate medical facilities and emergency services',
      color: theme.palette.error.main
    },
    {
      name: 'Restaurants',
      icon: <Restaurant />,
      description: 'Discover dining options in your vicinity',
      color: theme.palette.info.main
    },
    {
      name: 'Book Shops',
      icon: <Book />,
      description: 'Find literary stores and reading materials',
      color: theme.palette.secondary.main
    },
    {
      name: 'Pet Shops',
      icon: <Pets />,
      description: 'Locate pet supplies and animal services',
      color: theme.palette.warning.main
    },
    {
      name: 'Fruits & Vegetables',
      icon: <Spa />,
      description: 'Find fresh produce from local markets',
      color: theme.palette.success.main
    }
  ];

  // Key Benefits
  const benefits = [
    {
      icon: <Groups />,
      title: 'Community Focused',
      description: 'Connects local businesses with community members'
    },
    {
      icon: <Speed />,
      title: 'Time Saving',
      description: 'Quickly find what you need without extensive searching'
    },
    {
      icon: <ConnectWithoutContact />,
      title: 'Easy Access',
      description: 'All essential services available in one app'
    },
    {
      icon: <TrendingUp />,
      title: 'Local Support',
      description: 'Helps support and grow local businesses'
    }
  ];

  return (
    <Box sx={{
      bgcolor: '#f0f4f8', // Changed to light background
      color: '#0f172a', // Changed to dark text
      overflow: 'hidden'
    }}>
      <Navbar />
      {/* Hero Section */}
      <HeroSection sx={{
        minHeight: '80vh',
        backgroundImage: `linear-gradient(rgba(240, 244, 248, 0.9), rgba(240, 244, 248, 0.9)), url(${sdImage})`, // Light overlay
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center" sx={{ minHeight: '90vh', py: 10 }}>
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<Explore />}
                    label="LOCAL SERVICES DISCOVERY"
                    sx={{
                      background: alpha('#4551be', 0.1), // Changed to blue accent
                      color: '#4551be', // Changed to blue
                      border: `1px solid ${alpha('#4551be', 0.2)}`,
                      fontWeight: 600,
                      fontSize: '14px',
                      padding: '8px 16px',
                      mb: 4
                    }}
                  />

                  {/* Logo + Title in Same Row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {/* Logo */}
                    <Box
                      component="img"
                      src="/images/Thedal.png"
                      alt="Thedal App Logo"
                      sx={{
                        width: { xs: 70, md: 100 },
                        height: { xs: 70, md: 100 },
                        objectFit: 'contain',
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.7)', // Light background
                        p: 0.5,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    />

                    {/* Title */}
                    <Typography sx={{
                      fontSize: { xs: '2rem', md: '3rem' },
                      fontWeight: 800,
                      lineHeight: 1.1,
                      color: '#4551be', // Changed to blue
                    }}>
                      Thedal App
                      <Box component="span" sx={{
                        display: 'block',
                        fontWeight: 400,
                        color: '#64748b', // Dark grey
                        fontSize: { xs: '1.2rem', md: '1.6rem' },
                        mt: 1
                      }}>
                        Discover Nearby Amenities & Services
                      </Box>
                    </Typography>
                  </Box>

                  <Typography sx={{
                    color: '#475569', // Changed to dark grey
                    mb: 5,
                    fontSize: '1.1rem',
                    lineHeight: 1.6,
                    maxWidth: '90%'
                  }}>
                    The Thedal app provides convenience by offering information on nearby shops,
                    markets and hospitals at your fingertips. With just a few taps, users can
                    easily locate essential services in their vicinity.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box>
                  <Paper sx={{
                    background: 'rgba(255, 255, 255, 0.9)', // Light background
                    borderRadius: '24px',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    p: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)'
                  }}>
                    <Typography variant="h5" gutterBottom sx={{
                      fontWeight: 700,
                      mb: 4,
                      color: '#4551be' // Changed to blue
                    }}>
                      How Thedal Works
                    </Typography>

                    <Stack spacing={3}>
                      {[
                        {
                          step: '1',
                          title: 'Open the App',
                          description: 'Launch Thedal on your smartphone'
                        },
                        {
                          step: '2',
                          title: 'Allow Location Access',
                          description: 'Enable location services for accurate results'
                        },
                        {
                          step: '3',
                          title: 'Browse or Search',
                          description: 'Find services by category or specific search'
                        },
                        {
                          step: '4',
                          title: 'Get Directions',
                          description: 'Navigate to your chosen destination'
                        }
                      ].map((step, index) => (
                        <Box key={index} sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3
                        }}>
                          <Box sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background: alpha('#4551be', 0.1), // Changed to blue accent
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4551be', // Changed to blue
                            fontWeight: 700,
                            fontSize: '1.2rem'
                          }}>
                            {step.step}
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#1e293b' }}>
                              {step.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b' }}>
                              {step.description}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* App Overview */}
      <Box sx={{ py: { xs: 8, md: 12 } ,   bgcolor: '#f0f4f8',  // Changed to light background
  color: '#0f172a'}}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 800,
              mb: 3,
              color: '#1e293b' // Dark text
            }}>
              About Thedal App
            </Typography>
            <Typography sx={{
              color: '#64748b', // Dark grey
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.2rem'
            }}>
              Thedal is designed to help users discover and access local services with ease.
              Whether you're looking for groceries, medical assistance or dining options,
              Thedal simplifies the search process.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography variant="h3" sx={{
                    fontSize: '2rem',
                    fontWeight: 700,
                    mb: 4,
                    color: '#1e293b' // Dark text
                  }}>
                    Our Purpose
                  </Typography>

                  <Typography sx={{
                    color: '#64748b', // Dark grey
                    mb: 4,
                    lineHeight: 1.7,
                    fontSize: '1.1rem'
                  }}>
                    Thedal aims to make daily life more convenient by providing easy access
                    to nearby amenities. The app helps users save time and effort while
                    supporting local businesses and communities.
                  </Typography>

                  <List>
                    {[
                      'Provides information on nearby shops and services',
                      'Helps in emergencies by locating nearby hospitals',
                      'Supports local businesses and economies',
                      'Enhances community connectivity',
                      'Simplifies daily errands and tasks'
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 1.5 }}>
                        <ListItemIcon>
                          <Verified sx={{ color: '#4551be' }} /> {/* Changed to blue */}
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{ sx: { fontWeight: 500, color: '#1e293b' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box>
                  <FeatureCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{
                        fontWeight: 700,
                        mb: 3,
                        color: '#4551be' // Changed to blue
                      }}>
                        Key Benefits
                      </Typography>

                      <Grid container spacing={3}>
                        {benefits.map((benefit, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Box sx={{
                              textAlign: 'center',
                              p: 2,
                              borderRadius: '12px',
                              border: '1px solid',
                              borderColor: 'divider',
                              height: '100%'
                            }}>
                              <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '15px',
                                background: alpha('#4551be', 0.1), // Changed to blue accent
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px auto',
                                color: '#4551be' // Changed to blue
                              }}>
                                {benefit.icon}
                              </Box>
                              <Typography variant="h6" sx={{
                                fontWeight: 600,
                                mb: 1,
                                fontSize: '1.1rem',
                                color: '#1e293b' // Dark text
                              }}>
                                {benefit.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b' }}>
                                {benefit.description}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </FeatureCard>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        bgcolor: '#f0f4f8', // Changed to light background
        color: '#0f172a'
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 800,
              mb: 3,
              color: '#1e293b' // Dark text
            }}>
              Core Features
            </Typography>
            <Typography sx={{
              color: '#64748b', // Dark grey
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.2rem'
            }}>
              Thedal offers a range of features designed to enhance your local discovery experience
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Slide direction="up" in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <FeatureCard>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{
                        width: 70,
                        height: 70,
                        borderRadius: '18px',
                        background: alpha(feature.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px auto',
                        color: feature.color
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: '#1e293b' // Dark text
                      }}>
                        {feature.title}
                      </Typography>
                      <Typography sx={{
                        color: '#64748b', // Dark grey
                        lineHeight: 1.6,
                        fontSize: '0.95rem'
                      }}>
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Service Categories */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f0f4f8',  // Changed to light background
  color: '#0f172a' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3rem' },
              fontWeight: 800,
              mb: 3,
              color: '#1e293b' // Dark text
            }}>
              Service Categories
            </Typography>
            <Typography sx={{
              color: '#64748b', // Dark grey
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.2rem'
            }}>
              Discover various types of local services available through Thedal
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {categories.map((category, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Grow in timeout={1000} style={{ transitionDelay: `${index * 100}ms` }}>
                  <FeatureCard>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <CategoryIcon color={category.color}>
                        {category.icon}
                      </CategoryIcon>

                      <Typography variant="h6" sx={{
                        fontWeight: 700,
                        mb: 2,
                        mt: 3,
                        color: '#1e293b' // Dark text
                      }}>
                        {category.name}
                      </Typography>

                      <Typography sx={{
                        color: '#64748b', // Dark grey
                        lineHeight: 1.6,
                        fontSize: '0.95rem'
                      }}>
                        {category.description}
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Value Proposition */}
      <Box sx={{
        py: { xs: 10, md: 15 },
        background: `linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)`, // Changed to light background
        color: '#0f172a' // Changed to dark text
      }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 3,
              color: '#1e293b' // Dark text
            }}>
              Transforming Local Discovery
            </Typography>

            <Typography sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              fontSize: '1.2rem',
              lineHeight: 1.6,
              opacity: 0.9,
              color: '#64748b' // Dark grey
            }}>
              Thedal simplifies the way people find and access local services, making daily
              life more convenient while fostering community connections and supporting
              local businesses.
            </Typography>

            <Box sx={{
              maxWidth: 600,
              mx: 'auto',
              background: 'rgba(255, 255, 255, 0.7)', // Light background
              borderRadius: '12px',
              p: 4,
              border: '1px solid rgba(0,0,0,0.05)'
            }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <PinDrop sx={{ fontSize: 40, mb: 2, opacity: 0.9, color: '#4551be' }} /> {/* Changed to blue */}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1e293b' }}>
                      Easy Navigation
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, color: '#64748b' }}>
                      Find your way to local services effortlessly
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <QrCode sx={{ fontSize: 40, mb: 2, opacity: 0.9, color: '#4551be' }} /> {/* Changed to blue */}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1e293b' }}>
                      Quick Access
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8, color: '#64748b' }}>
                      Access information with simple interactions
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>
      <HomeFooter />
    </Box>
  );
};

export default ThedalOverview;